import capitalize from 'lodash/capitalize';
import { useState } from 'react';
import { Sun, Moon, Menu, BrainCircuit } from 'lucide-react';
import { useAuth } from '@chainlit/react-client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { useTheme } from '@/components/ThemeProvider';
import { AppSidebarContent } from './AppSidebar';

interface AppTopBarProps {
  title: string;
}

const ROLE_CONFIG: Record<string, { label: string; className: string }> = {
  admin:  { label: '管理員', className: 'bg-primary/20 text-primary border-primary/30 hover:bg-primary/20' },
  user:   { label: '使用者', className: 'bg-blue-500/20 text-blue-600 border-blue-500/30 hover:bg-blue-500/20 dark:text-blue-400' },
  viewer: { label: '訪客',   className: 'bg-gray-500/20 text-gray-600 border-gray-500/30 hover:bg-gray-500/20 dark:text-gray-400' },
};

export default function AppTopBar({ title }: AppTopBarProps) {
  const { user } = useAuth();
  const { variant, setTheme } = useTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const displayName = user?.display_name || user?.identifier || '';
  const role = (user?.metadata as Record<string, unknown>)?.role as string | undefined;
  const roleInfo = ROLE_CONFIG[role || ''] || ROLE_CONFIG['user'];

  return (
    <div className="flex items-center justify-between h-[60px] px-4 md:px-6 bg-card border-b border-border flex-shrink-0">
      <div className="flex items-center gap-2 min-w-0">
        {/* 行動版（< md）：漢堡選單，開啟側欄抽屜（導覽 + 對話紀錄 + 登出） */}
        <button
          onClick={() => setMobileNavOpen(true)}
          className="md:hidden p-2 -ml-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          aria-label="開啟選單"
        >
          <Menu className="size-5" />
        </button>
        <h1 className="text-base font-semibold text-foreground truncate">{title}</h1>
      </div>

      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col">
          <SheetHeader className="px-4 py-3 border-b shrink-0">
            <SheetTitle className="flex items-center gap-2 text-sm font-semibold">
              <BrainCircuit className="text-primary size-5" />
              EdgeMindAI
            </SheetTitle>
          </SheetHeader>
          <AppSidebarContent onNavigate={() => setMobileNavOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex items-center gap-3">
        <button
          onClick={() => setTheme(variant === 'dark' ? 'light' : 'dark')}
          className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          title={variant === 'dark' ? '切換至明亮模式' : '切換至暗黑模式'}
        >
          {variant === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </button>

        {user && (
          <>
            <Badge className={`${roleInfo.className} text-xs font-medium`}>
              {roleInfo.label}
            </Badge>
            <span className="text-sm text-muted-foreground">{displayName}</span>
            <Avatar className="h-8 w-8 ring-2 ring-primary/40">
              <AvatarImage src={user?.metadata?.image} alt="user image" />
              <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-sm">
                {capitalize(displayName?.[0] || 'U')}
              </AvatarFallback>
            </Avatar>
          </>
        )}
      </div>
    </div>
  );
}
